import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../contexts/AuthContext';
import { Plus, MoreHorizontal, Pencil, Trash2, Users, Shield, UserPlus, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESET_COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#84cc16',
  '#10b981', '#06b6d4', '#3b82f6', '#6366f1',
  '#8b5cf6', '#d946ef', '#ec4899', '#64748b',
];

export default function DepartmentsPage() {
  const { token, user: currentUser } = useAuth();
  const [departments, setDepartments] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isMembersOpen, setIsMembersOpen] = useState(false);
  const [selectedDept, setSelectedDept] = useState(null);
  const [deptMembers, setDeptMembers] = useState([]);
  const [formData, setFormData] = useState({ name: '', description: '', color: '#6366f1' });

  const canManage = currentUser?.role === 'owner' || currentUser?.role === 'admin';
  const isOwner = currentUser?.role === 'owner';

  const fetchDepartments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/departments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDepartments(res.data);
    } catch {
      toast.error('Erreur lors du chargement des departements');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllUsers(res.data);
    } catch {
      // silent
    }
  }, [token]);

  useEffect(() => {
    if (canManage) {
      fetchDepartments();
      fetchUsers();
    }
  }, [canManage, fetchDepartments, fetchUsers]);

  const resetForm = () => setFormData({ name: '', description: '', color: '#6366f1' });

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/departments`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Departement cree avec succes');
      setIsCreateOpen(false);
      resetForm();
      fetchDepartments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la creation');
    }
  };

  const openEdit = (dept) => {
    setSelectedDept(dept);
    setFormData({ name: dept.name, description: dept.description || '', color: dept.color });
    setIsEditOpen(true);
  };

  const handleUpdate = async () => {
    try {
      await axios.patch(`${API}/departments/${selectedDept.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Departement mis a jour');
      setIsEditOpen(false);
      setSelectedDept(null);
      fetchDepartments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce departement ? Les membres ne seront pas supprimes.')) return;
    try {
      await axios.delete(`${API}/departments/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Departement supprime');
      fetchDepartments();
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const openMembers = async (dept) => {
    setSelectedDept(dept);
    try {
      const res = await axios.get(`${API}/departments/${dept.id}/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeptMembers(res.data.map(m => m.id));
    } catch {
      setDeptMembers([]);
    }
    setIsMembersOpen(true);
  };

  const toggleMember = (userId) => {
    setDeptMembers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const saveMembers = async () => {
    try {
      await axios.put(`${API}/departments/${selectedDept.id}/members`, {
        user_ids: deptMembers
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Membres mis a jour');
      setIsMembersOpen(false);
      fetchDepartments();
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  if (!canManage) {
    return (
      <DashboardLayout title="Departements">
        <Card className="p-8 text-center">
          <Shield className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium">Acces refuse</h3>
          <p className="text-muted-foreground mt-2">Vous n'avez pas les permissions necessaires.</p>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Departements">
      <div className="space-y-6" data-testid="departments-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-muted-foreground">Gerez les departements et assignez les collaborateurs</p>
          </div>
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="create-department-btn">
                <Plus className="w-4 h-4" />
                Nouveau departement
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Creer un departement</DialogTitle>
                <DialogDescription>Ajoutez un nouveau departement a votre organisation</DialogDescription>
              </DialogHeader>
              <DeptForm formData={formData} setFormData={setFormData} />
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Annuler</Button>
                <Button onClick={handleCreate} disabled={!formData.name} data-testid="save-department-btn">
                  Creer
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Departments Grid */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Chargement...</div>
        ) : departments.length === 0 ? (
          <Card className="p-12 text-center" data-testid="empty-departments">
            <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">Aucun departement</h3>
            <p className="text-muted-foreground mt-2 mb-4">Creez votre premier departement pour organiser vos equipes</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {departments.map(dept => (
              <DeptCard
                key={dept.id}
                dept={dept}
                allUsers={allUsers}
                isOwner={isOwner}
                onEdit={() => openEdit(dept)}
                onDelete={() => handleDelete(dept.id)}
                onManageMembers={() => openMembers(dept)}
              />
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Modifier le departement</DialogTitle>
              <DialogDescription>Mettez a jour les informations du departement</DialogDescription>
            </DialogHeader>
            <DeptForm formData={formData} setFormData={setFormData} />
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditOpen(false)}>Annuler</Button>
              <Button onClick={handleUpdate} disabled={!formData.name} data-testid="update-department-btn">
                Mettre a jour
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Members Dialog */}
        <Dialog open={isMembersOpen} onOpenChange={setIsMembersOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: selectedDept?.color }} />
                Membres — {selectedDept?.name}
              </DialogTitle>
              <DialogDescription>Cochez les collaborateurs a assigner a ce departement</DialogDescription>
            </DialogHeader>
            <div className="max-h-80 overflow-y-auto space-y-1 py-2">
              {allUsers.map(u => (
                <label
                  key={u.id}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-accent cursor-pointer transition-colors"
                  data-testid={`member-toggle-${u.id}`}
                >
                  <Checkbox
                    checked={deptMembers.includes(u.id)}
                    onCheckedChange={() => toggleMember(u.id)}
                  />
                  <Avatar className="h-7 w-7">
                    <AvatarFallback className="text-xs bg-primary/10 text-primary">
                      {`${u.first_name?.[0] || ''}${u.last_name?.[0] || ''}`.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{u.first_name} {u.last_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                  </div>
                  <Badge variant="secondary" className="text-[10px]">{u.role}</Badge>
                </label>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsMembersOpen(false)}>Annuler</Button>
              <Button onClick={saveMembers} data-testid="save-members-btn">
                Enregistrer ({deptMembers.length} membre{deptMembers.length !== 1 ? 's' : ''})
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

function DeptForm({ formData, setFormData }) {
  return (
    <div className="space-y-4 py-4">
      <div className="space-y-2">
        <Label htmlFor="dept-name">Nom *</Label>
        <Input
          id="dept-name"
          placeholder="Ex: Commercial, RH, Comptabilite..."
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          data-testid="dept-name-input"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="dept-desc">Description</Label>
        <Input
          id="dept-desc"
          placeholder="Description du departement (optionnel)"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          data-testid="dept-description-input"
        />
      </div>
      <div className="space-y-2">
        <Label>Couleur</Label>
        <div className="flex flex-wrap gap-2">
          {PRESET_COLORS.map(c => (
            <button
              key={c}
              type="button"
              className={`w-7 h-7 rounded-full border-2 transition-all ${formData.color === c ? 'border-foreground scale-110' : 'border-transparent'}`}
              style={{ backgroundColor: c }}
              onClick={() => setFormData({ ...formData, color: c })}
              data-testid={`color-${c}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function DeptCard({ dept, allUsers, isOwner, onEdit, onDelete, onManageMembers }) {
  const members = allUsers.filter(u => u.department_ids?.includes(dept.id));

  return (
    <Card className="group hover:shadow-md transition-shadow" data-testid={`dept-card-${dept.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 rounded-full shrink-0" style={{ backgroundColor: dept.color }} />
            <CardTitle className="text-base">{dept.name}</CardTitle>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`dept-actions-${dept.id}`}>
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onEdit} data-testid={`edit-dept-${dept.id}`}>
                <Pencil className="w-4 h-4 mr-2" /> Modifier
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onManageMembers} data-testid={`manage-members-${dept.id}`}>
                <UserPlus className="w-4 h-4 mr-2" /> Gerer les membres
              </DropdownMenuItem>
              {isOwner && (
                <DropdownMenuItem onClick={onDelete} className="text-destructive" data-testid={`delete-dept-${dept.id}`}>
                  <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {dept.description && (
          <p className="text-sm text-muted-foreground mt-1">{dept.description}</p>
        )}
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {dept.member_count} membre{dept.member_count !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex -space-x-2">
            {members.slice(0, 4).map(m => (
              <Avatar key={m.id} className="h-7 w-7 border-2 border-background">
                <AvatarFallback className="text-[10px]" style={{ backgroundColor: dept.color + '20', color: dept.color }}>
                  {`${m.first_name?.[0] || ''}${m.last_name?.[0] || ''}`.toUpperCase()}
                </AvatarFallback>
              </Avatar>
            ))}
            {members.length > 4 && (
              <Avatar className="h-7 w-7 border-2 border-background">
                <AvatarFallback className="text-[10px] bg-muted text-muted-foreground">
                  +{members.length - 4}
                </AvatarFallback>
              </Avatar>
            )}
          </div>
        </div>
        <Button variant="outline" size="sm" className="w-full mt-3 gap-2" onClick={onManageMembers} data-testid={`btn-manage-members-${dept.id}`}>
          <UserPlus className="w-3.5 h-3.5" />
          Gerer les membres
        </Button>
      </CardContent>
    </Card>
  );
}
